// external library imports
import React, { useState, useContext, useRef } from 'react'
import { isEmpty } from 'lodash-es'
import { A11yDialog } from 'react-a11y-dialog'
import classNames from 'classnames'

// React components
import EventZone from './event-zone'
import EventTime from './event-time'
import PricingBlock from './pricing-block'

// helper methods
import {
  hasPriceTypes,
  totalTickets,
  groupByType,
  isToday,
  filterCurrentDayZonesWithinCutOff
} from '../helpers/logic'
import { buildCartEvent, combineTickets } from '../helpers/cart-builder'
import { extractPricing } from '../../shared/helpers/logic'

// React app contexts
import { TicketsAppContext } from '../context/tickets-app-context'

const renderPricing = event => {
  const pricing = extractPricing(event)
  if (!isEmpty(pricing)) {
    return groupByType(pricing).map(([key, value]) => {
      return (
        <PricingBlock
          key={key}
          price={value}
          member={key === 'member'}
          isDynamicPricing={event.isDynamicPricing}
        />
      )
    })
  }
}

// this is a thunk - a function that returns a function. this is done so we can pass
// `hasAvailability(minimumAvailability)` to a filter function as a callback
const hasAvailability = minimumAvailability => event =>
  event.availability >= minimumAvailability

const renderZones = (
  event,
  selectedZone,
  onSelectZone,
  minimumAvailability,
  fromEvent
) => {
  const { productId, performances } = event

  if (event.performances[0].zones.length === 0) {
    console.warn(
      'Performances array has no zones for event' + event.name + ' ' + event.id
    )
  }
  if (event.displayMultipleZones && event.performances[0].zones.length > 1) {
    let sortedZones = performances[0].zones.sort(
      (a, b) => a.zoneRank - b.zoneRank
    )
    if (isToday(event.occursAt)) {
      sortedZones = filterCurrentDayZonesWithinCutOff(sortedZones)
    }

    sortedZones = sortedZones.filter(
      zone => zone.availableCount > 0 && zone.zoneTime
    )

    if (fromEvent) {
      // SHEDDWEB-123: this filter was using hasAvailability before,
      // which checks an `availability` property, not `availableCount`
      sortedZones = sortedZones.filter(
        ({ availableCount }) => availableCount >= minimumAvailability
      )
    }

    return sortedZones.map((zoneInstance, index) => {
      const selected = selectedZone === zoneInstance
      return (
        <EventZone
          key={`zone-${index}`}
          productId={productId}
          selected={selected}
          availableSpots={zoneInstance.availableCount}
          onSelectZone={onSelectZone(zoneInstance)}
          zoneInstance={zoneInstance}
          isDynamicPricing={event.isDynamicPricing}
        />
      )
    })
  }
}

const renderTimes = (
  event,
  selectedTime,
  onSelectTime,
  minimumAvailability,
  fromEvent
) => {
  const { productId, times, hideProductInventory } = event
  if (times) {
    let sortedTimes = times.sort(
      (a, b) => new Date(a.occursAt) - new Date(b.occursAt)
    )
    if (fromEvent) {
      sortedTimes = sortedTimes.filter(hasAvailability(minimumAvailability))
    }
    return sortedTimes.map((timeInstance, index) => {
      const selected = selectedTime === timeInstance
      return (
        <EventTime
          key={`time-${index}`}
          productId={productId}
          selected={selected}
          availableSpots={timeInstance.availability}
          onSelectTime={onSelectTime(timeInstance)}
          timeInstance={timeInstance}
          hideProductInventory={hideProductInventory}
          isDynamicPricing={event.isDynamicPricing}
        />
      )
    })
  }
}

const addToCartAndContinue =
  (addItemsToCart, cart, events) => async newCartEvent => {
    let newCartItems = [newCartEvent]
    await addItemsToCart(newCartItems)
  }

function EventBody({ event, visible }) {
  const {
    actions: { addItemsToCart, selectEvent },
    chosenEvent,
    cart,
    events,
    minimumAvailability,
    fromEvent
  } = useContext(TicketsAppContext)
  const [time, selectTime] = useState(null)
  const [zone, selectZone] = useState(null)
  const [loading, setLoading] = useState(false)
  const dialogRef = useRef(null)
  const { name, description, pricing, learnMoreText, isDynamicPricing } = event
  const eventToCheck = isEmpty(pricing) ? event.times[0] : event
  const hasMultipleTimesButNoneSelected = !isEmpty(event.times) && !time
  const hasMultipleZonesButNoneSelected =
    event.displayMultipleZones &&
    event.performances[0].zones.length > 1 &&
    !zone
  let isAddOn = false
  if (!eventToCheck) {
    console.warn(
      `${event.name} has an empty pricing object and is not a scheduled event`
    )
  } else {
    isAddOn = hasPriceTypes(['addon'], eventToCheck)
  }

  const style = {
    transition: 'opacity 300ms linear',
    opacity: visible ? 1 : 0
  }

  const adderFunc = addToCartAndContinue(addItemsToCart, cart, events.events)

  const onSelectZone = zone => e => {
    if (e.currentTarget.checked) {
      selectZone(zone)
    } else {
      selectZone(null)
    }
  }

  const onSelectTime = time => e => {
    if (e.currentTarget.checked) {
      selectTime(time)
    } else {
      selectTime(null)
    }
  }

  const onClickSelectEvent = adderFunc => async _ => {
    const fromEvent = new URLSearchParams(window.location.search).get(
      'event_id'
    )
    setLoading(true)
    if (event.displayMultipleZones && event.performances[0].zones.length > 1) {
      if (!zone) return
      const selectedEvent = event
      selectedEvent.chosenZone = zone
      let scheduledEvent = buildCartEvent([], {
        ...selectedEvent
      })
      if (chosenEvent && chosenEvent.productId === scheduledEvent.productId) {
        scheduledEvent = {
          ...scheduledEvent,
          tickets: combineTickets(scheduledEvent.tickets, chosenEvent.tickets)
        }
      }
      if (fromEvent && totalTickets(chosenEvent.tickets) !== 0) {
        await adderFunc(scheduledEvent)
      }
      selectEvent(scheduledEvent)
      setLoading(false)
    } else if (event.times.length !== 0) {
      if (!time) return
      const selectedEvent = event
      let scheduledEvent = buildCartEvent([], {
        ...selectedEvent,
        ...time
      })
      if (chosenEvent && chosenEvent.productId === scheduledEvent.productId) {
        scheduledEvent = {
          ...scheduledEvent,
          tickets: combineTickets(scheduledEvent.tickets, chosenEvent.tickets)
        }
      }
      if (fromEvent && totalTickets(chosenEvent.tickets) !== 0) {
        await adderFunc(scheduledEvent)
      }
      selectEvent(scheduledEvent)
      setLoading(false)
    } else {
      if (chosenEvent && chosenEvent.productId === event.productId) {
        if (fromEvent && totalTickets(chosenEvent.tickets) !== 0) {
          await adderFunc(chosenEvent, event)
        }
        event = { ...event, tickets: chosenEvent.tickets }
      }
      selectEvent(event)
      setLoading(false)
    }
  }

  const onOpenDialog = () => {
    dialogRef.current.show()
  }

  const onCloseDialog = () => {
    dialogRef.current && dialogRef.current.hide()
  }

  let learnMoreButtonHidden = true

  if (isDynamicPricing) {
    learnMoreButtonHidden = false
  } else if (
    learnMoreText === null ||
    learnMoreText === '<p><br></p>' ||
    learnMoreText === ''
  ) {
    learnMoreButtonHidden = true
  } else {
    learnMoreButtonHidden = false
  }

  return (
    <React.Fragment>
      <div
        className="-mx-4 p-4 overflow-hidden"
        hidden={!visible}
        style={style}
      >
        <p
          className="text-16 leading-normal"
          dangerouslySetInnerHTML={{ __html: description }}
        >
          {}
        </p>
        <p className="my-8 text-16 leading-normal">
          <span>
            <button
              type="button"
              className='text-link'
              onClick={onOpenDialog}
              hidden={learnMoreButtonHidden}
            >
              {isDynamicPricing ? (
                <>See resident and member pricing ></>
              ) : (
                <>
                  Learn More
                  <span className="sr-only"> about {event.name}</span>.{' '}
                </>
              )}
            </button>
          </span>
        </p>

        {!isDynamicPricing && (
          <ul className="my-24 text-14">
            {renderPricing(event, isDynamicPricing)}
          </ul>
        )}

        <ul
          className="my-24 text-18"
          data-testid={`event-zones-${name.toLowerCase().replace(' ', '-')}`}
        >
          {renderZones(
            event,
            zone,
            onSelectZone,
            minimumAvailability,
            fromEvent
          )}
        </ul>

        <ul className="my-24 text-18">
          {renderTimes(
            event,
            time,
            onSelectTime,
            minimumAvailability,
            fromEvent
          )}
        </ul>

        <button
          type="button"
          className={classNames('button -big', {
            '-disabled':
              hasMultipleTimesButNoneSelected ||
              hasMultipleZonesButNoneSelected,
            'cursor-not-allowed':
              hasMultipleTimesButNoneSelected || hasMultipleZonesButNoneSelected
          })}
          onClick={onClickSelectEvent(adderFunc)}
          disabled={
            hasMultipleTimesButNoneSelected || hasMultipleZonesButNoneSelected
          }
        >
          {loading ? 'Loading...' : `Select ${name}`}
        </button>
        {isAddOn && (
          <p className="xs-paragraph">
            {name} requires a General Admission ticket. General Admission will
            be added to your cart.
          </p>
        )}
      </div>
      <A11yDialog
        id={`learn-more-text`}
        dialogRoot="#tickets-dialogs"
        dialogRef={ref => (dialogRef.current = ref)}
        classNames={{
          container: 'dialog',
          overlay: 'dialog-overlay',
          dialog: 'dialog-content',
          title: 'dialog-title',
          closeButton: 'dialog-close'
        }}
        title={name}
      >
        <button
          type="button"
          className="dialog-close"
          onClick={onCloseDialog}
        >
          <span aria-hidden="true">×</span>
          <span className="sr-only">Close this dialog window</span>
        </button>
        <div
          className="my-8 text-16 leading-normal"
          dangerouslySetInnerHTML={{
            __html: learnMoreText
          }}
        />
        {isDynamicPricing && (
          <ul className="my-24 text-14">
            {renderPricing(event, isDynamicPricing)}
          </ul>
        )}
      </A11yDialog>
    </React.Fragment>
  )
}

export default EventBody
